import { useState, useEffect, useRef } from 'react'

/**
 * A simplified popper hook that positions an element relative to a reference element
 * Only supports 'top' and 'bottom' placements
 *
 * @param {React.RefObject|HTMLElement|null} referenceElement - The reference element
 * @param {React.RefObject|HTMLElement|null} popperElement - The popper element
 * @param {Object} options - Configuration options
 * @param {string} [options.placement='bottom'] - Placement of the popper ('top' or 'bottom')
 * @param {string} [options.strategy='absolute'] - Positioning strategy ('absolute' or 'fixed')
 * @param {number} [options.offset=8] - Offset from reference element in pixels
 * @param {Function} [options.onFirstUpdate] - Callback after first position update
 * @returns {Object} Popper state and styles
 */
const useSimplePopper = (referenceElement, popperElement, options = {}) => {
  const { placement = 'bottom', strategy = 'absolute', offset = 8, onFirstUpdate } = options

  const [styles, setStyles] = useState({
    popper: {
      position: strategy,
      left: '0',
      top: '0',
    },
  })

  const firstUpdateRef = useRef(true)
  const updateRef = useRef(() => {})

  // Helper to get the actual DOM element from a ref or the element itself
  const getElement = elementOrRef => {
    if (!elementOrRef) return null
    return elementOrRef.current || elementOrRef
  }

  // Calculate position based on placement (only top or bottom)
  const calculatePosition = (referenceEl, popperEl, placement) => {
    if (!referenceEl || !popperEl) return { top: 0, left: 0 }

    const referenceRect = referenceEl.getBoundingClientRect()
    const popperRect = popperEl.getBoundingClientRect()
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop

    // Horizontal positioning - always center-aligned
    const left = referenceRect.left + (referenceRect.width - popperRect.width) / 2

    // Vertical positioning based on placement
    let top
    if (placement === 'top') {
      top = referenceRect.top - popperRect.height - offset
    } else {
      // Default is 'bottom'
      top = referenceRect.bottom + offset
    }

    if (strategy === 'fixed') {
      return { top, left }
    }

    // Add scroll position for 'absolute' strategy
    return {
      top: top + scrollTop,
      left: left + scrollLeft,
    }
  }

  // Ensure the element stays within viewport bounds
  const adjustForViewport = (position, popperEl) => {
    if (!popperEl) return position

    const { top, left } = position
    const popperRect = popperEl.getBoundingClientRect()
    const viewportWidth = document.documentElement.clientWidth
    const viewportHeight = document.documentElement.clientHeight
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    const positionPadding = 0
    let adjustedLeft = left
    let adjustedTop = top

    // Horizontal adjustment - keep centered if possible, but avoid viewport overflow
    if (left < positionPadding + scrollLeft) {
      adjustedLeft = positionPadding + scrollLeft
    } else if (left + popperRect.width > viewportWidth - positionPadding + scrollLeft) {
      adjustedLeft = viewportWidth - positionPadding - popperRect.width + scrollLeft
    }

    // Vertical adjustment - flip placement if needed
    if (placement === 'top' && top < positionPadding + scrollTop) {
      // If it would overflow at the top, flip to bottom
      const referenceEl = getElement(referenceElement)
      const referenceRect = referenceEl.getBoundingClientRect()
      adjustedTop = referenceRect.bottom + offset + scrollTop
    } else if (placement === 'bottom' && top + popperRect.height > viewportHeight - positionPadding + scrollTop) {
      // If it would overflow at the bottom, flip to top
      const referenceEl = getElement(referenceElement)
      const referenceRect = referenceEl.getBoundingClientRect()
      adjustedTop = referenceRect.top - popperRect.height - offset + scrollTop
    }

    return { top: adjustedTop, left: adjustedLeft }
  }

  // Update the position
  const updatePosition = () => {
    const referenceEl = getElement(referenceElement)
    const popperEl = getElement(popperElement)

    if (!referenceEl || !popperEl) return

    const position = calculatePosition(referenceEl, popperEl, placement)
    const adjustedPosition = adjustForViewport(position, popperEl)

    // Update styles
    setStyles({
      popper: {
        position: strategy,
        top: `${adjustedPosition.top}px`,
        left: `${adjustedPosition.left}px`,
      },
    })

    if (firstUpdateRef.current) {
      firstUpdateRef.current = false
      if (onFirstUpdate) onFirstUpdate()
    }
  }

  // Store update function in ref to avoid recreating effect
  updateRef.current = updatePosition

  // Set up event listeners and update position
  useEffect(() => {
    const referenceEl = getElement(referenceElement)
    const popperEl = getElement(popperElement)

    if (!referenceEl || !popperEl) return

    // Initial position update
    updateRef.current()

    // Setup event listeners for repositioning
    const handleUpdate = () => updateRef.current()

    window.addEventListener('scroll', handleUpdate, true)
    window.addEventListener('resize', handleUpdate)

    return () => {
      window.removeEventListener('scroll', handleUpdate, true)
      window.removeEventListener('resize', handleUpdate)
    }
  }, [referenceElement, popperElement])

  // Update when placement or strategy changes
  useEffect(() => {
    updateRef.current()
  }, [placement, strategy, offset])

  return {
    styles,
    update: updateRef.current,
    forceUpdate: updateRef.current,
  }
}

export default useSimplePopper
